import { Component, DestroyRef, inject, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSelect } from '@angular/material/select';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { faChevronLeft, faPlusCircle, faTrashCan } from '@fortawesome/free-solid-svg-icons';
import { BehaviorSubject, EMPTY, first, Observable } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { UserService } from 'src/app/core/services/user.service';
import { indicate } from 'src/app/operators';
import { MultiSelectFilterComponent } from 'src/app/search/filters/multi-select/multi-select-filter/multi-select-filter.component';
import { ConfirmDialogComponent } from 'src/app/shared/confirm-dialog/confirm-dialog.component';
import { CustomAlertDialogComponent } from 'src/app/shared/custom-alert-dialog/custom-alert-dialog.component';
import {
  ApprovalState,
  EsraPipingAPIClient,
  ExecuteReleaseRequestDto,
  FileResponse,
  IReleaseObjectGroupRoleViewModel,
  ReleaseAttributesDto,
  ReleaseComponent,
  ReleaseDetailViewModel,
  ReleaseFinalChecksSummaryDto,
  ReleaseObjectGroupRoleViewModel,
  ReleaseObjectGroupViewModel,
  ReleaseRoleViewModel,
  ReleaseSelectedObject,
  ReleaseStatus,
  RevisionStatus,
  RoleViewModel,
  UserType,
} from 'src/app/shared/models/autogenerated-piping';
import { ReleaseLifecycleChange, ReleaseLifecycleService } from 'src/app/shared/services/release-lifecycle.service';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';
import { getApiErrorMessage } from 'src/app/shared/utils';
import {
  AddClassToReleaseDialogComponent,
  AddClassToReleaseDialogResult,
} from './add-class-to-release-dialog/add-class-to-release-dialog.component';
import { ReleaseCommentsTabSummary } from './release-comments-tab/release-comments-tab.component';
import { ReleaseRolesSavePayload } from './release-roles-tab/release-roles-tab.component';
import {
  RestoreDraftDialogComponent,
  RestoreDraftDialogData,
  RestoreDraftDialogResult,
} from './restore-draft-dialog/restore-draft-dialog.component';

/** Sentinel value used by the backend for an unset `Guid` field. */
const EMPTY_GUID: string = '00000000-0000-0000-0000-000000000000';

/**
 * Snapshot of a row's lifecycle action fields captured immediately before a
 * `saveReleaseObjectGroup` call. Used to roll back the UI if the request fails,
 * keeping the displayed state consistent with what the server actually has.
 */
interface RowActionSnapshot {
  action: ReleaseSelectedObject | undefined;
  restorePreviousDraft: boolean | null | undefined;
}

interface ReleaseSpecRow {
  /**
   * The underlying object group from the release detail. Radio actions mutate
   * `objectGroup.action` directly, so the parent `release.objects` array stays in
   * sync and can be sent straight back to the save endpoint without remapping.
   */
  objectGroup: ReleaseObjectGroupViewModel;
  /** Stable id used for radio-group naming and selection tracking. */
  id: string;
  /** Pipe class name (PIPE release) or valve tag (VALVE release). */
  specificationCode: string;
  /** Object type derived from the release component (PIPE / VALVE). */
  objectType: 'Pipe' | 'Valve';
  /** Target object id for navigation — the copied object when present, else the original. */
  navigationId: string | undefined;
  /** Aggregated reviewer approval state used for the status column and filter. */
  reviewStatus: string;
  author: string;
  createdDate: Date | undefined;
  isSelected: boolean;
  /** True while a `saveReleaseObjectGroup` API call for this row is in flight. */
  saving: boolean;
}

class Filter {
  filterName: string;
  options: string[];

  constructor(filterName: string, options: string[]) {
    this.filterName = filterName;
    this.options = options;
  }
}

@Component({
  selector: 'app-release-details',
  templateUrl: './release-details.component.html',
  styleUrls: ['./release-details.component.scss'],
  standalone: false,
})
export class ReleaseDetailsComponent implements OnInit {
  readonly displayedColumns: string[] = [
    'select',
    'specificationCode',
    'reviewStatus',
    'author',
    'createdDate',
    'releaseAsIs',
    'retire',
    'revise',
    'info',
  ];

  // Icons
  readonly faChevronLeft = faChevronLeft;
  readonly faPlusCircle = faPlusCircle;
  readonly faTrashCan = faTrashCan;

  /** Exposes the action enum to the template for radio/bulk-select bindings. */
  readonly ReleaseSelectedObject = ReleaseSelectedObject;
  /** Exposes the status enum to the template for the draft/review toggle button. */
  readonly ReleaseStatus = ReleaseStatus;

  /** Passed to `app-export-excel-button`; the download filename comes from the response's content-disposition header. */
  exportReleaseToExcelFn = (): Observable<FileResponse> => this.esraPipingAPIClient.releaseToExcel(this.release?.id ?? '');

  /** Id of the currently expanded info row; null when no row is expanded. */
  expandedRowId: string | null = null;

  loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  releaseRolesSaving$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  changingStatus$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  /** True while a Valve release is being published via `onValidateAndRelease`. */
  publishingRelease$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  release: ReleaseDetailViewModel | null = null;
  releaseSpecsCount: number = 0;

  /** Final-checks summary of a Valve release awaiting publish; null until loaded or when not applicable. */
  valvePublishSummary: ReleaseFinalChecksSummaryDto | null = null;

  /** Label for the comments tab, e.g. "comments (3 - Open)". Updated via the tab's summaryChanged output. */
  commentsTabLabel: string = 'comments';

  filters: Filter[] = [];
  searchText: string = '';

  readonly pageSize: number = 10;
  readonly pageSizes: number[] = [10, 25, 50, 100, 500];

  /** Table data source — sort and paginator are wired in ngAfterViewInit. */
  dataSource: MatTableDataSource<ReleaseSpecRow> = new MatTableDataSource<ReleaseSpecRow>([]);

  private allSpecs: ReleaseSpecRow[] = [];
  private filterSelections: Map<string, Set<string>> = new Map();
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly releaseLifecycleService: ReleaseLifecycleService = inject(ReleaseLifecycleService);

  @ViewChild(MatSort)
  set matSort(sort: MatSort | undefined) {
    if (sort) {
      this.dataSource.sort = sort;
    }
  }

  @ViewChild(MatPaginator)
  set matPaginator(paginator: MatPaginator | undefined) {
    if (paginator) {
      this.dataSource.paginator = paginator;
    }
  }

  @ViewChild('bulkStatusSelect') bulkStatusSelect: MatSelect | undefined;
  @ViewChildren(MultiSelectFilterComponent) childFilters?: QueryList<MultiSelectFilterComponent>;

  /** Label for the draft/review toggle button, based on the current release status. */
  get reviewStatusToggleLabel(): string {
    return this.release?.status === ReleaseStatus.REVIEW ? 'Return to draft' : 'confirm info to review';
  }

  /**
   * Valve releases publish directly (no Final System Checks flow yet); Pipe releases still
   * validate first. While a Pipe release is still in Draft, publishing isn't allowed yet, so
   * the button reads "validate" (dropping the "and release" part) until it reaches Review.
   */
  get validateAndReleaseButtonLabel(): string {
    if (this.release?.component === ReleaseComponent._1) {
      return 'publish release';
    }

    return this.release?.status === ReleaseStatus.DRAFT ? 'validate' : 'validate and release';
  }

  /** Number of rows currently selected by checkbox. */
  get selectedCount(): number {
    return this.allSpecs.filter((row: ReleaseSpecRow) => row.isSelected).length;
  }

  /** Number of rows currently shown in the table after filters/search. */
  get displayedRowCount(): number {
    return this.dataSource.data.length;
  }

  /** True when every currently displayed row is selected. */
  get isAllDisplayedSelected(): boolean {
    if (this.displayedRowCount === 0) {
      return false;
    }

    return this.dataSource.data.every((row: ReleaseSpecRow) => row.isSelected);
  }

  /** True when some, but not all, currently displayed rows are selected. */
  get isSelectAllIndeterminate(): boolean {
    if (this.displayedRowCount === 0) {
      return false;
    }

    const selectedDisplayedCount: number = this.dataSource.data.filter((row: ReleaseSpecRow) => row.isSelected).length;
    return selectedDisplayedCount > 0 && selectedDisplayedCount < this.displayedRowCount;
  }

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly esraPipingAPIClient: EsraPipingAPIClient,
    private readonly snackBar: SnackBarService,
    private readonly dialog: MatDialog,
    private readonly userService: UserService,
  ) {}

  ngOnInit(): void {
    this.loadReleaseDetail();

    // A pipe class / valve editor open in another tab can move the row's lifecycle when it saves.
    this.releaseLifecycleService.changes$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((change: ReleaseLifecycleChange) => this.applyLifecycleChangeFromEditor(change));
  }

  /**
   * Brings the matching row in line with a lifecycle change saved in an editor tab.
   * Patches the fields the editor controls and falls back to a full reload when the row is unknown
   * (e.g. added from another tab).
   */
  private applyLifecycleChangeFromEditor(change: ReleaseLifecycleChange): void {
    if (!this.release?.id || change.releaseId !== this.release.id) { return; }

    const row: ReleaseSpecRow | undefined = this.allSpecs.find((spec: ReleaseSpecRow) => spec.id === change.objectGroupId);
    if (!row) {
      this.loadReleaseDetail();
      return;
    }

    // Skip rows with an in-flight save of their own; its response is the newer truth.
    if (row.saving) { return; }

    row.objectGroup.action = change.action;
    row.objectGroup.newObjectId = change.newObjectId;
    row.objectGroup.hasPreservedDraft = change.hasPreservedDraft;
    row.objectGroup.reviewStatus = change.reviewStatus;
    if (change.roles) {
      row.objectGroup.roles = change.roles.map((role: IReleaseObjectGroupRoleViewModel) => ReleaseObjectGroupRoleViewModel.fromJS(role));
    }
    // The editor already resolved any preserved-draft decision, so a stale local choice must not be re-sent.
    row.objectGroup.restorePreviousDraft = undefined;
    row.reviewStatus = this.getApprovalSummary(row.objectGroup);
    row.navigationId = this.resolveNavigationId(row.objectGroup);

    // Same reasoning as saveRowAction: the open info panel caches data tied to the previous clone.
    if (this.expandedRowId === row.id) {
      this.expandedRowId = null;
      setTimeout(() => { this.expandedRowId = row.id; });
    }
    this.applyAllFilters();
  }

  /**
   * True when the currently logged-in user is either the release author or one of
   * its owners (`UserType._1`). Non-author/non-owner users can view the release but
   * must not see or use the roles tab, add-to-release, validate-and-release, or
   * lifecycle action controls.
   */
  get canManageRelease(): boolean {
    const currentOid: string | undefined = this.userService.getCurrentUser()?.idTokenClaims?.['oid'] as string | undefined;
    if (!currentOid) { return false; }

    if (this.release?.authorOid && this.release.authorOid === currentOid) {
      return true;
    }

    const roles: ReleaseRoleViewModel[] = this.release?.roles ?? [];
    return roles.some((role: ReleaseRoleViewModel) => role.userRole === UserType._1 && role.userId === currentOid);
  }

  /**
   * True once a release is Published and the current user only has access via the
   * Admin or Piping.Revision.Reader role (not as an author/owner). Published releases
   * are final, so these viewers get a read-only page: filters and downloads stay
   * enabled, but lifecycle actions and any other data-changing controls are disabled.
   */
  get isReadOnlyMode(): boolean {
    return this.release?.status === ReleaseStatus.PUBLISHED;
  }

  /** True when the current user can perform lifecycle/data-changing actions on this release. */
  get canModifyRelease(): boolean {
    return this.canManageRelease && !this.isReadOnlyMode;
  }

  /**
   * Valve releases keep the Draft -> Review -> Publish flow, so the button only
   * appears once a Valve release reaches Review.
   * Pipe releases can also validate from Draft
   * (the Final System Checks page itself blocks publishing until the release is out of Draft).
   */
  get canValidateOrPublishRelease(): boolean {
    if (!this.canModifyRelease) { return false; }

    if (this.release?.component === ReleaseComponent._1) {
      return this.release?.status === ReleaseStatus.REVIEW || this.release?.status === ReleaseStatus.APPROVAL;
    }

    return this.release?.status === ReleaseStatus.DRAFT
      || this.release?.status === ReleaseStatus.REVIEW
      || this.release?.status === ReleaseStatus.APPROVAL;
  }

  /**
   * True when a Valve release fails the publish validations (same summary the Pipe flow uses on its
   * Final System Checks page). The server enforces it again on publish.
   */
  get isPublishBlockedByChecks(): boolean {
    return this.release?.component === ReleaseComponent._1 && this.valvePublishSummary?.canPublish === false;
  }

  /**
   * Toggles the info panel for the given row. Collapses when the same row is
   * already expanded; otherwise expands it (the child component handles loading).
   */
  toggleInfoRow(row: ReleaseSpecRow): void {
    this.expandedRowId = this.expandedRowId === row.id ? null : row.id;
  }

  backToReleases(): void {
    this.router.navigate(['/piping/revisionsAndReleases/releases']);
  }

  /** Updates the comments tab label with the current comment count and overall status. */
  onCommentsSummaryChanged(summary: ReleaseCommentsTabSummary): void {
    const statusLabel: string = summary.hasOpenComments ? 'Open' : 'Resolved';
    this.commentsTabLabel = `comments (${summary.totalCount} - ${statusLabel})`;
  }

  toggleSpecSelection(row: ReleaseSpecRow): void {
    row.isSelected = !row.isSelected;
  }

  /** Toggles selection for all rows currently displayed in the table. */
  toggleSelectAllDisplayed(): void {
    const shouldSelectAll: boolean = !this.isAllDisplayedSelected;
    this.dataSource.data.forEach((row: ReleaseSpecRow) => {
      row.isSelected = shouldSelectAll;
    });
  }

  /**
   * Handles a radio-button change for a single row. If switching to Revise and the
   * object has a preserved draft without a restore/reset decision, the user is
   * prompted before saving. Otherwise the action is persisted immediately.
   * Ignored when the row is already mid-save to prevent concurrent requests.
   */
  updateLifecycleAction(row: ReleaseSpecRow, action: ReleaseSelectedObject): void {
    if (row.saving || !this.canModifyRelease) { return; }

    // Capture the current state before any mutation so it can be restored if the save fails.
    const snapshot: RowActionSnapshot = {
      action: row.objectGroup.action,
      restorePreviousDraft: row.objectGroup.restorePreviousDraft,
    };

    const needsDecision: boolean =
      action === ReleaseSelectedObject._1
      && row.objectGroup.hasPreservedDraft === true
      && row.objectGroup.restorePreviousDraft == null; // null from server or undefined after clearing

    if (needsDecision) {
      this.openRestoreDraftDialog(row.specificationCode, 1).subscribe((result: RestoreDraftDialogResult | undefined) => {
        if (result === undefined) { return; } // User cancelled — leave the action unchanged.
        row.objectGroup.restorePreviousDraft = result === 'restore';
        row.objectGroup.action = action;
        this.saveRowAction(row, snapshot);
      });
    } else {
      if (action !== ReleaseSelectedObject._1) {
        // Clear the stored decision when moving away from Revise so the user
        // is prompted again if they switch back.
        row.objectGroup.restorePreviousDraft = undefined;
      }
      row.objectGroup.action = action;
      this.saveRowAction(row, snapshot);
    }
  }

  /** Opens the correct detail page for the selected row in a new browser tab. */
  openSpecInNewTab(row: ReleaseSpecRow): void {
    if (!row.navigationId) { return; }

    const url: string = this.releaseLifecycleService.getEditorUrl(row.objectType, row.navigationId);
    window.open(url, '_blank');
  }

  /**
   * Applies a lifecycle action to all currently selected rows and persists each
   * one via `saveReleaseObjectGroup`. Rows that are already mid-save are skipped.
   * When setting to Revise and some selected rows have a preserved draft without
   * a decision, the user is prompted once before any row is saved.
   */
  setActionForSelected(action: ReleaseSelectedObject): void {
    if (!this.canModifyRelease) { return; }

    const selectedRows: ReleaseSpecRow[] = this.allSpecs.filter(
      (row: ReleaseSpecRow) => row.isSelected && !row.saving
    );
    if (selectedRows.length === 0) { return; }

    if (action === ReleaseSelectedObject._1) {
      const rowsNeedingDecision: ReleaseSpecRow[] = selectedRows.filter(
        (row: ReleaseSpecRow) => row.objectGroup.hasPreservedDraft === true && row.objectGroup.restorePreviousDraft == null
      );

      if (rowsNeedingDecision.length > 0) {
        // Show the dialog once, representing all affected rows.
        // The spec name of the first affected row is shown in the title.
        this.openRestoreDraftDialog(rowsNeedingDecision[0].specificationCode, rowsNeedingDecision.length)
          .subscribe((result: RestoreDraftDialogResult | undefined) => {
            if (result === undefined) { return; } // Cancelled — do not change any row.
            selectedRows.forEach((row: ReleaseSpecRow) => {
              // Snapshot captured here, before mutation, on each iteration.
              const snapshot: RowActionSnapshot = { action: row.objectGroup.action, restorePreviousDraft: row.objectGroup.restorePreviousDraft };
              row.objectGroup.action = action;
              if (row.objectGroup.hasPreservedDraft === true && row.objectGroup.restorePreviousDraft == null) {
                row.objectGroup.restorePreviousDraft = result === 'restore';
              }
              this.saveRowAction(row, snapshot);
            });
          });
      } else {
        selectedRows.forEach((row: ReleaseSpecRow) => {
          const snapshot: RowActionSnapshot = { action: row.objectGroup.action, restorePreviousDraft: row.objectGroup.restorePreviousDraft };
          row.objectGroup.action = action;
          this.saveRowAction(row, snapshot);
        });
      }
    } else {
      selectedRows.forEach((row: ReleaseSpecRow) => {
        const snapshot: RowActionSnapshot = { action: row.objectGroup.action, restorePreviousDraft: row.objectGroup.restorePreviousDraft };
        row.objectGroup.action = action;
        row.objectGroup.restorePreviousDraft = undefined;
        this.saveRowAction(row, snapshot);
      });
    }
  }

  onBulkStatusChange(action: ReleaseSelectedObject): void {
    this.setActionForSelected(action);
    // Use setTimeout so the reset runs after the current event cycle finishes;
    // this guarantees the mat-select panel is closed before we clear its value,
    // making the same option re-selectable on the very next interaction.
    setTimeout(() => {
      if (this.bulkStatusSelect) {
        this.bulkStatusSelect.value = null;
      }
    });
  }

  /** Removes all checkbox-selected rows from the current draft release. */
  removeSelectedFromRelease(): void {
    if (!this.release?.id || !this.canModifyRelease) {
      return;
    }

    const selectedObjectIds: string[] = this.allSpecs
      .filter((row: ReleaseSpecRow) => row.isSelected)
      .map((row: ReleaseSpecRow) => row.objectGroup.id)
      .filter((id: string | undefined): id is string => !!id);

    if (selectedObjectIds.length === 0) {
      return;
    }

    const uniqueObjectIds: string[] = [...new Set(selectedObjectIds)];
    const removeCall = this.release.component === ReleaseComponent._1
      ? this.esraPipingAPIClient.removeValvesFromRelease(this.release.id, uniqueObjectIds)
      : this.esraPipingAPIClient.removePipeClassesFromRelease(this.release.id, uniqueObjectIds);

    removeCall
      .pipe(
        indicate(this.loading$),
        first(),
        catchError((error: unknown) => {
          const errorMessage: string = getApiErrorMessage(error, 'Failed to remove selected item(s) from release. Please try again.');
          this.snackBar.showError(errorMessage);
          return EMPTY;
        })
      )
      .subscribe(() => {
        this.snackBar.showInfo(`Removed ${uniqueObjectIds.length} item(s) from release.`);
        this.loadReleaseDetail();
      });
  }

  onCheckboxChange(selectedValues: Set<string>, filter: Filter): void {
    if (selectedValues.size === 0) {
      this.filterSelections.delete(filter.filterName);
    } else {
      this.filterSelections.set(filter.filterName, new Set(selectedValues));
    }
    this.applyAllFilters();
  }

  callSearch(term: string): void {
    this.searchText = term;
    this.applyAllFilters();
  }

  clearAllFilters(): void {
    this.childFilters?.forEach((childFilter: MultiSelectFilterComponent) => childFilter.clearSelections());
    this.filterSelections.clear();
    this.searchText = '';
    this.applyAllFilters();
  }



  /**
   * Pipe releases navigate to the Final System Checks page. Valve releases don't have that
   * flow yet, so they confirm with the user and publish the release directly.
   */
  onValidateAndRelease(): void {
    if (!this.canValidateOrPublishRelease || this.isPublishBlockedByChecks) { return; }

    const releaseId: string = this.release?.id ?? '';
    if (!releaseId) { return; }

    if (this.release?.component === ReleaseComponent._1) {
      this.confirmAndPublishValveRelease(releaseId);
      return;
    }

    this.router.navigate(['/piping/releases', releaseId, 'final-checks']);
  }

  /** Confirms with the user before publishing a Valve release directly via `POST /release/{id}/publish`. */
  private confirmAndPublishValveRelease(releaseId: string): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '450px',
      data: {
        dialogMessage: 'This will publish this release. Do you wish to continue?',
        confirmText: 'yes',
        cancelText: 'cancel',
      },
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.publishValveRelease(releaseId);
      }
    });
  }

  /**
   * Publishes a Valve release. Fetches the current `ReleaseAttributesDto` first -- same as the
   * Final System Checks flow -- so the release name and initial revision name are sent along
   * with the publish request instead of being left blank.
   */
  private publishValveRelease(releaseId: string): void {
    const currentOid: string | undefined = this.userService.getCurrentUser()?.idTokenClaims?.['oid'] as string | undefined;

    this.esraPipingAPIClient.getReleaseAttributes(releaseId)
      .pipe(
        switchMap((attributes: ReleaseAttributesDto) => {
          const dto: ExecuteReleaseRequestDto = new ExecuteReleaseRequestDto({
            attributes,
            acknowledgedValidationCodes: [],
            actorOid: currentOid ?? '',
          });
          return this.esraPipingAPIClient.publishRelease(releaseId, dto);
        }),
        indicate(this.publishingRelease$),
        first(),
      )
      .subscribe({
        next: () => {
          this.snackBar.showInfo('Release published successfully.');
          this.router.navigate(['/piping/revisionsAndReleases/releases']);
        },
        error: (error: unknown) => {
          if ((error as { status?: number })?.status === 409) {
            // 409 carries the blocking summary; the attributes check is the only one a Valve release runs.
            this.snackBar.showError('Release blocked: the release name and revision name must be filled in before publishing.');
            this.loadValvePublishSummary();
            return;
          }
          const errorMessage: string = getApiErrorMessage(error, 'Failed to publish release. Please try again.');
          this.snackBar.showError(errorMessage);
        },
      });
  }

  /**
   * Moves the release between DRAFT and REVIEW status via `POST /Release/{id}/status`.
   * Confirms with the user before advancing to REVIEW, since this puts every pipe class
   * and valve in the release into review status at once.
   */
  onToggleReviewStatus(): void {
    const releaseId: string = this.release?.id ?? '';
    if (!releaseId || !this.canModifyRelease || this.release?.status === undefined || this.release?.status === null) {
      return;
    }

    const targetStatus: ReleaseStatus = this.release.status === ReleaseStatus.REVIEW ? ReleaseStatus.DRAFT : ReleaseStatus.REVIEW;

    if (targetStatus === ReleaseStatus.REVIEW) {
      // Release object type applies uniformly to every object group in the release (PIPE or VALVE).
      const objectTypePlural: string = this.release.component === ReleaseComponent._1 ? 'valves' : 'pipe classes';
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        width: '450px',
        data: {
          dialogMessage: `This will move all ${objectTypePlural} in this release into review status. Do you wish to continue?`,
          confirmText: 'yes',
          cancelText: 'cancel',
        },
      });

      dialogRef.afterClosed().subscribe((confirmed: boolean) => {
        if (confirmed) {
          this.changeReleaseStatus(releaseId, targetStatus);
        }
      });
      return;
    }

    this.changeReleaseStatus(releaseId, targetStatus);
  }

  /** Calls `changeReleaseStatus` and applies the returned release detail on success. */
  private changeReleaseStatus(releaseId: string, targetStatus: ReleaseStatus): void {
    this.esraPipingAPIClient.changeReleaseStatus(releaseId, targetStatus)
      .pipe(indicate(this.changingStatus$), first())
      .subscribe({
        next: (updatedRelease: ReleaseDetailViewModel) => {
          this.applyReleaseDetail(updatedRelease);

          if (targetStatus === ReleaseStatus.REVIEW) {
            const releaseName: string = updatedRelease.name || this.release?.name || 'this';
            this.dialog.open(CustomAlertDialogComponent, {
              width: '500px',
              data: {
                title: 'release confirmed',
                message: `Thank you for completing the release for ${releaseName} specification. \nAn email will be sent to the reviewer(s) to begin the review process.`,
                buttonText: 'done',
              },
            });
          }
        },
        error: (error: unknown) => {
          const errorMessage: string = getApiErrorMessage(error, 'Failed to update release status. Please try again.');
          this.snackBar.showError(errorMessage);
        },
      });
  }

  /**
   * Receives role user ids from the roles tab and persists them using
   * `PUT /Release/{id}/members`.
   */
  onSaveReleaseRoles(payload: ReleaseRolesSavePayload): void {
    const releaseId: string = this.release?.id ?? '';
    if (!releaseId || !this.canModifyRelease) {
      return;
    }

    if (payload.releaseOwners.length === 0) {
      this.snackBar.showError('At least one release owner is required.');
      return;
    }

    const members: RoleViewModel[] = this.buildReleaseMembersPayload(payload);
    this.esraPipingAPIClient.updateReleaseMembers(releaseId, members)
      .pipe(indicate(this.releaseRolesSaving$), first())
      .subscribe({
        next: () => {
          this.snackBar.showInfo('Release roles saved successfully.');
          this.loadReleaseDetail();
        },
        error: (error: unknown) => {
          const errorMessage: string = getApiErrorMessage(error, 'Failed to save release roles. Please try again.');
          this.snackBar.showError(errorMessage);
        },
      });
  }

  /** Opens the add-to-release dialog and saves selected pipe classes or valves to this release. */
  onAddClassToRelease(): void {
    if (!this.canModifyRelease) { return; }
    if (!this.release?.id || this.release.component === undefined || this.release.component === null) {
      return;
    }

    const assignedObjectIds: string[] = this.collectAssignedObjectIds();
    const dialogRef = this.dialog.open(AddClassToReleaseDialogComponent, {
      width: '94vw',
      maxWidth: '1500px',
      maxHeight: '86vh',
      autoFocus: false,
      data: {
        releaseComponent: this.release.component,
        assignedObjectIds,
        release: this.release,
      },
    });

    dialogRef.afterClosed().subscribe((result: AddClassToReleaseDialogResult | undefined) => {
      const selectedObjectIds: string[] = result?.selectedObjectIds ?? [];
      if (selectedObjectIds.length === 0) {
        return;
      }

      // The dialog handled the API call, so just reload the release data.
      this.loadReleaseDetail();
    });
  }

  getReleaseStatusLabel(statusCode: ReleaseStatus | undefined): string {
    switch (statusCode) {
      case ReleaseStatus.DRAFT: return 'Draft';
      case ReleaseStatus.REVIEW: return 'In Review';
      case ReleaseStatus.APPROVAL: return 'Ready to Publish';
      case ReleaseStatus.PUBLISHED: return 'Published';
      case ReleaseStatus.RETIRED: return 'Retired';
      case ReleaseStatus.REJECTED: return 'Rejected';
      default: return 'Unknown';
    }
  }

  /** Same status colors as the releases list; statuses without a list color keep the default badge. */
  getReleaseStatusClass(statusCode: ReleaseStatus | undefined): string {
    switch (statusCode) {
      case ReleaseStatus.DRAFT: return 'status-draft';
      case ReleaseStatus.REVIEW: return 'status-in-review';
      case ReleaseStatus.APPROVAL: return 'status-ready-to-publish';
      case ReleaseStatus.PUBLISHED: return 'status-published';
      default: return '';
    }
  }

  /** Human-readable label for the release component (object) type. */
  getComponentLabel(component: ReleaseComponent | undefined): string {
    switch (component) {
      case ReleaseComponent._0: return 'Pipe';
      case ReleaseComponent._1: return 'Valve';
      default: return 'N/A';
    }
  }

  private loadReleaseDetail(): void {
    const releaseId: string = this.route.snapshot.paramMap.get('releaseId') ?? '';
    if (!releaseId) {
      return;
    }

    this.esraPipingAPIClient.getReleaseById(releaseId)
      .pipe(indicate(this.loading$), first())
      .subscribe((release: ReleaseDetailViewModel) => {
        this.applyReleaseDetail(release);
      });
  }

  /** Stores the loaded release and rebuilds the table rows and filters. */
  private applyReleaseDetail(release: ReleaseDetailViewModel): void {
    this.release = release;
    this.allSpecs = this.buildRowsFromReleaseObjects(release.objects ?? []);
    this.releaseSpecsCount = this.allSpecs.length;
    this.initializeFilters();
    this.applyAllFilters();
    this.loadValvePublishSummary();
  }

  /** Loads the publish validations for a Valve release that can be published; clears them otherwise. */
  private loadValvePublishSummary(): void {
    const releaseId: string | undefined = this.release?.id;
    const isPublishable: boolean = this.release?.component === ReleaseComponent._1
      && (this.release?.status === ReleaseStatus.REVIEW || this.release?.status === ReleaseStatus.APPROVAL);
    if (!releaseId || !isPublishable) {
      this.valvePublishSummary = null;
      return;
    }

    this.esraPipingAPIClient.getSummary(releaseId)
      .pipe(first(), catchError(() => EMPTY))
      .subscribe((summary: ReleaseFinalChecksSummaryDto) => {
        this.valvePublishSummary = summary;
      });
  }

  private buildRowsFromReleaseObjects(objects: ReleaseObjectGroupViewModel[]): ReleaseSpecRow[] {
    // The component type applies to every object group in the release (PIPE or VALVE).
    const objectType: 'Pipe' | 'Valve' = this.release?.component === ReleaseComponent._1 ? 'Valve' : 'Pipe';
    const author: string = this.release?.author ?? 'N/A';
    const createdDate: Date | undefined = this.release?.creationDate;

    return objects.map((objectGroup: ReleaseObjectGroupViewModel) => {
      const objectName: string = objectGroup.objectName ?? 'N/A';
      // Valve object names may include an internal suffix (e.g. TAG_123).
      // Show only the human-readable tag prefix in the release details table.
      const specificationCode: string = objectType === 'Valve'
        ? (objectName.split('_')[0] || objectName)
        : objectName;

      return {
      objectGroup,
      id: objectGroup.id ?? '',
      specificationCode,
      objectType,
      navigationId: this.resolveNavigationId(objectGroup),
      reviewStatus: this.getApprovalSummary(objectGroup),
      author,
      createdDate,
      isSelected: false,
      saving: false,
    };
  })
  }

  /**
   * Returns the object id used to open the spec detail page. Prefers the new (copied)
   * object created inside the release revision; falls back to the original source object
   * when the copy has not been created yet (e.g. an unmodified "release as-is" row).
   */
  private resolveNavigationId(objectGroup: ReleaseObjectGroupViewModel): string | undefined {
    if (objectGroup.newObjectId && objectGroup.newObjectId !== EMPTY_GUID) {
      return objectGroup.newObjectId;
    }
    if (objectGroup.originalObjectId && objectGroup.originalObjectId !== EMPTY_GUID) {
      return objectGroup.originalObjectId;
    }
    return undefined;
  }

  /**
   * Collapses the per-role approval states of an object group into a single status label.
   * Uses the server-computed `reviewStatus` to detect Draft — checking `roles.length === 0`
   * is fragile because pseudo-statuses computed by the backend may also arrive with no roles.
   * Rejections take precedence over all other non-Draft states.
   */
  private getApprovalSummary(objectGroup: ReleaseObjectGroupViewModel): string {
    // RevisionStatus.DRAFT = DRAFT
    if (objectGroup.reviewStatus === RevisionStatus.DRAFT) {
      return 'Draft';
    }
    const roles: ReleaseObjectGroupRoleViewModel[] = objectGroup.roles ?? [];
    if (roles.some((role: ReleaseObjectGroupRoleViewModel) => role.approvalState === ApprovalState._2)) {
      return 'Review With Rejections';
    }
    if (roles.some((role: ReleaseObjectGroupRoleViewModel) => role.approvalState === ApprovalState._4)) {
      return 'Review With Pending Comments';
    }
    if(this.release?.status===ReleaseStatus.PUBLISHED && roles.every((role: ReleaseObjectGroupRoleViewModel) =>
      role.approvalState === ApprovalState._3)) {
      return 'Published';
    }
    if (roles.every((role: ReleaseObjectGroupRoleViewModel) =>
      role.approvalState === ApprovalState._1 || role.approvalState === ApprovalState._3)) {
      return 'Ready to Publish';
    }
    return 'In Review';
  }

  private initializeFilters(): void {
    const unique = (items: string[]): string[] => [...new Set(items.filter(Boolean))];

    const reviewStatuses: string[] = unique(this.allSpecs.map((row: ReleaseSpecRow) => row.reviewStatus));
    const authors: string[] = unique(this.allSpecs.map((row: ReleaseSpecRow) => row.author));
    const lifeCycles: string[] = ['release as is', 'retire', 'revise'];
    const commentStates: string[] = ['Has comments', 'No comments'];

    this.filters = [];
    if (reviewStatuses.length > 0) {
      this.filters.push(new Filter('Review status', reviewStatuses));
    }
    if (lifeCycles.length > 0) {
      this.filters.push(new Filter('Life cycle', lifeCycles));
    }
    if (authors.length > 0) {
      this.filters.push(new Filter('Author', authors));
    }
    this.filters.push(new Filter('Comments', commentStates));
  }

  private applyAllFilters(): void {
    let result: ReleaseSpecRow[] = [...this.allSpecs];

    for (const [filterName, selectedValues] of this.filterSelections) {
      if (selectedValues.size === 0) {
        continue;
      }

      result = result.filter((row: ReleaseSpecRow) => {
        if (filterName === 'Review status') {
          return selectedValues.has(row.reviewStatus);
        }
        if (filterName === 'Life cycle') {
          return selectedValues.has(this.getActionDisplayValue(row.objectGroup.action));
        }
        if (filterName === 'Author') {
          return selectedValues.has(row.author);
        }
        if (filterName === 'Comments') {
          return selectedValues.has(row.objectGroup.hasComments ? 'Has comments' : 'No comments');
        }
        return true;
      });
    }

    if (this.searchText) {
      const term: string = this.searchText.toLowerCase();
      result = result.filter((row: ReleaseSpecRow) => (
        row.specificationCode.toLowerCase().includes(term)
        || row.reviewStatus.toLowerCase().includes(term)
        || row.author.toLowerCase().includes(term)
      ));
    }

    // Hand the filtered result to MatTableDataSource; it handles sorting and pagination natively.
    this.dataSource.data = result;
  }

  /** Maps the action enum to the display value used by the Life cycle filter. */
  private getActionDisplayValue(action: ReleaseSelectedObject | undefined): string {
    if (action === ReleaseSelectedObject._2) {
      return 'retire';
    }
    if (action === ReleaseSelectedObject._1) {
      return 'revise';
    }
    return 'release as is';
  }

  /** Collects current object ids in the release to block duplicate selection in the add dialog. */
  private collectAssignedObjectIds(): string[] {
    if (!this.release?.objects || this.release.objects.length === 0) {
      return [];
    }

    const assignedObjectIds: string[] = [];
    for (const objectGroup of this.release.objects) {
      if (objectGroup.newObjectId && objectGroup.newObjectId !== EMPTY_GUID) {
        assignedObjectIds.push(objectGroup.newObjectId);
      }
      if (objectGroup.originalObjectId && objectGroup.originalObjectId !== EMPTY_GUID) {
        assignedObjectIds.push(objectGroup.originalObjectId);
      }
    }

    return [...new Set(assignedObjectIds)];
  }

  /**
   * Maps role-group user ids from the Roles tab to `RoleViewModel` objects.
   * The backend owns row identity and object-level sync, so members are sent
   * as non-author role records with default pending approval state.
   */
  private buildReleaseMembersPayload(payload: ReleaseRolesSavePayload): RoleViewModel[] {
    const members: RoleViewModel[] = [];
    const dedupeKeys: Set<string> = new Set<string>();

    const appendMembers = (userIds: string[], userRole: UserType): void => {
      userIds.forEach((userId: string) => {
        const trimmedUserId: string = (userId ?? '').trim();
        if (!trimmedUserId) {
          return;
        }

        const dedupeKey: string = `${trimmedUserId}-${userRole}`;
        if (dedupeKeys.has(dedupeKey)) {
          return;
        }
        dedupeKeys.add(dedupeKey);

        members.push(new RoleViewModel({
          userId: trimmedUserId,
          // Release-level member endpoint does not target a single revision id.
          revisionId: 0,
          userRole,
          approvalState: ApprovalState._0,
        }));
      });
    };

    appendMembers(payload.releaseOwners, UserType._1);
    appendMembers(payload.internalReviewers, UserType._2);
    appendMembers(payload.contributors, UserType._3);

    return members;
  }


  /**
   * Calls `saveReleaseObjectGroup` for a single row, marks it as saving while
   * the request is in-flight, and merges the server-returned view model back into
   * the row so that server-computed fields (e.g. `hasPreservedDraft`) stay current.
   * On failure, the pre-save action state is restored from `snapshot` so the
   * radio buttons reflect what the server actually has.
   */
  private saveRowAction(row: ReleaseSpecRow, snapshot: RowActionSnapshot): void {
    if (!this.release?.id || !row.objectGroup.id) { return; }

    row.saving = true;
    this.esraPipingAPIClient.saveReleaseObjectGroup(this.release.id, row.objectGroup.id, row.objectGroup)
      .pipe(first())
      .subscribe({
        next: (updated: ReleaseObjectGroupViewModel) => {
          row.objectGroup = updated;
          row.reviewStatus = this.getApprovalSummary(updated);
          row.navigationId = this.resolveNavigationId(updated);
          row.saving = false;
          this.releaseLifecycleService.announceChange(updated, this.release?.id);
          // If the info panel for this row is currently open, force it to reload by
          // destroying and recreating the component via *ngIf. The revision id may have
          // changed (e.g. a new copy was created when switching to Revise), so the
          // cached comments in the existing component instance would be stale.
          if (this.expandedRowId === row.id) {
            this.expandedRowId = null;
            setTimeout(() => { this.expandedRowId = row.id; });
          }
          this.applyAllFilters();
        },
        error: () => {
          // Restore the pre-save state so the UI stays consistent with the server.
          row.objectGroup.action = snapshot.action;
          row.objectGroup.restorePreviousDraft = snapshot.restorePreviousDraft == null
            ? undefined
            : snapshot.restorePreviousDraft;
          row.saving = false;
          this.applyAllFilters();
          this.snackBar.showError('Failed to save. Please try again.');
        },
      });
  }

  /**
   * Opens the restore-draft prompt and returns the user's choice as an observable.
   * Emits `undefined` when the user cancels, in which case no save must be made.
   * @param specificationCode Name of the first (or only) affected spec, shown in the title.
   * @param affectedCount Total number of affected specs (1 for single-row, >1 for bulk).
   */
  private openRestoreDraftDialog(specificationCode: string, affectedCount: number): Observable<RestoreDraftDialogResult | undefined> {
    const data: RestoreDraftDialogData = { specificationCode, affectedCount };
    const dialogRef = this.dialog.open<RestoreDraftDialogComponent, RestoreDraftDialogData, RestoreDraftDialogResult>(
      RestoreDraftDialogComponent,
      { data, width: '500px', maxHeight: '90vh', disableClose: true }
    );
    return dialogRef.afterClosed();
  }
}
